import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '@/config';

interface FooterLink {
  label: string;
  path: string;
  external?: string;
  /** GA4/GTMクリック計測用（対象CTAのみ付与） */
  ctaName?: string;
  ctaLocation?: string;
}

const FOOTER_LINKS: Record<string, FooterLink[]> = {
  '製品': [
    { label: '機能一覧', path: '/features/' },
    { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
    { label: 'AIガントチャート', path: '/features/ai-gantt/' },
    { label: 'AI日報', path: '/features/ai-daily-report/' },
    { label: 'AIファイル管理', path: '/features/ai-file-management/' },
    { label: 'チーム機能', path: '/features/team-collaboration/' },
    { label: '多言語対応', path: '/features/multilingual/' },
  ],
  '料金・導入': [
    { label: '料金', path: '/pricing/' },
    { label: '無料トライアル', path: '/free-trial/', ctaName: 'free_trial', ctaLocation: 'footer' },
    { label: 'セキュリティ', path: '/security/' },
    { label: 'お問い合わせ', path: '/contact/' },
    { label: 'デモ・導入相談', path: '/contact/', ctaName: 'demo_contact', ctaLocation: 'footer' },
  ],
  'お役立ち情報': [
    { label: 'プロジェクト管理入門', path: '/guides/project-management/' },
    { label: 'プロジェクト管理とAI', path: '/guides/project-management-ai/' },
    { label: 'WBSとは', path: '/guides/wbs/' },
    { label: 'ガントチャートとは', path: '/guides/gantt-chart/' },
    { label: 'ツール比較', path: '/compare/project-management-tools/' },
    { label: 'Excelテンプレート', path: '/templates/project-management-excel/' },
  ],
  'サポート': [
    { label: 'お問い合わせ', path: '/contact/' },
    { label: '利用規約', path: '/terms/' },
    { label: 'プライバシーポリシー', path: '/privacy/' },
    { label: '特定商取引法に基づく表記', path: '/commercial-law/' },
    { label: 'ログイン', path: '#', external: SITE_CONFIG.appUrl, ctaName: 'login', ctaLocation: 'footer' },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-background-900 text-background-200">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {Object.entries(FOOTER_LINKS).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-background-50 mb-4">{category}</h4>
              <ul className="space-y-2">
                {links.map((link) => {
                  // GA4/GTM計測用data属性（対象CTAのみ）
                  const ctaAttrs: Record<string, string> = {};
                  if (link.ctaName) ctaAttrs['data-cta-name'] = link.ctaName;
                  if (link.ctaLocation) ctaAttrs['data-cta-location'] = link.ctaLocation;
                  return (
                    <li key={link.label}>
                      {link.external ? (
                        <a href={link.external} className="text-sm text-background-400 hover:text-background-100 transition-colors" rel="nofollow" {...ctaAttrs}>
                          {link.label}
                        </a>
                      ) : (
                        <Link to={link.path} className="text-sm text-background-400 hover:text-background-100 transition-colors" {...ctaAttrs}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-background-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/brand/favicon.png" alt="" className="w-7 h-7 rounded" />
            <span className="font-heading font-bold text-background-100">OMOIMUSUBI ”おもいむすび”</span>
          </div>
          <p className="text-sm text-background-500">
            &copy; {new Date().getFullYear()} {SITE_CONFIG.companyName}
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-background-400 hover:text-background-100 transition-colors" aria-label="X (Twitter)">
              <i className="ri-twitter-x-line text-lg"></i>
            </a>
            <a href="#" className="text-background-400 hover:text-background-100 transition-colors" aria-label="LinkedIn">
              <i className="ri-linkedin-line text-lg"></i>
            </a>
          </div>
        </div>

        <div className="mt-8 p-6 bg-background-800/50 rounded-lg">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-background-300">{SITE_CONFIG.companyName}</p>
              <p className="text-xs text-background-500 mt-1">{SITE_CONFIG.companyAddress}</p>
            </div>
            <div className="text-xs text-background-500">
              {SITE_CONFIG.companyPhone && <p>TEL: {SITE_CONFIG.companyPhone}</p>}
              {SITE_CONFIG.companyEmail && <p>Email: {SITE_CONFIG.companyEmail}</p>}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
